import { Container, JoinTeamSection, SocialsSection } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import ReadMoreReact from 'read-more-react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { PEOPLE_IMAGES } from './assets';
import linkedIn from './assets/compressed/linkedIn.webp';
import * as Styled from './styled';
import type { TeamPageProps } from './types';

export * from './types';

export const TeamPage: FC<TeamPageProps> = (props) => {
  const { joinTeamLink } = props;

  const { translate } = useTranslation();

  const persons = useMemo(() => {
    const data = Object.values(translate('team.people', { returnObjects: true }) ?? {});
    return data.map((item: any) => ({
      name: item.name,
      role: item.role,
      description: item.description,
      linkedIn: item.linkedIn ?? undefined,
      website: item.website ?? undefined,
    }));
  }, [translate]);

  return (
    <>
      <Styled.HeaderWrapper>
        <Styled.StyledHeaderProfile title={translate('team.title')} />
      </Styled.HeaderWrapper>
      <Container>
        <Styled.ItemContainer>
          {persons.map((person, index) => {
            return (
              <Styled.ItemWrapper key={index} orderReversed={index % 2 !== 0}>
                <Styled.ItemImage src={PEOPLE_IMAGES[index]} alt="team image" />
                <Styled.ItemContentWrapper>
                  <Styled.ItemTitleWrapper>
                    <Styled.ItemTitle>{person.name}</Styled.ItemTitle>
                    {person.linkedIn && (
                      <a href={person.linkedIn} target="_blank" rel="noreferrer">
                        <Styled.SocialIconImage src={linkedIn} alt="linkedIn icon" />
                      </a>
                    )}
                    {person.website && (
                      <a href={person.website} target="_blank" rel="noreferrer">
                        <Styled.IconWrapper>
                          <FaExternalLinkAlt />
                        </Styled.IconWrapper>
                      </a>
                    )}
                  </Styled.ItemTitleWrapper>
                  <Styled.ItemSubtitle>{person.role}</Styled.ItemSubtitle>
                  <Styled.ItemText>
                    <ReadMoreReact
                      key={person.description}
                      text={person.description}
                      min={200}
                      ideal={200}
                      max={9999}
                      readMoreText={`${translate('common.buttons.readMore')}...`}
                    />
                  </Styled.ItemText>
                </Styled.ItemContentWrapper>
              </Styled.ItemWrapper>
            );
          })}
        </Styled.ItemContainer>
        <JoinTeamSection link={joinTeamLink} />
        <Styled.SocialsWrapper>
          <SocialsSection />
        </Styled.SocialsWrapper>
      </Container>
    </>
  );
};
